import React from 'react';
import styled from '@emotion/styled';
import { COLORS } from '@chronos/constants';
import { Text } from '../../components';
import media from '../../shared/media';
import { AppleLogoIcon, AndroidLogoIcon, StarIcon, ArrowLeftIcon } from '../../shared/icons';

function StarRow() {
  return (
    <StarRowContainer>
      <StarIcon /><StarIcon /><StarIcon /><StarIcon /><StarIcon />
    </StarRowContainer>
  );
}

const slideLoiterTimes = [4000, 10000, 4000, 4000];
let timeoutHandle: number = 0;

export default class WelcomeSlides extends React.Component<propTypes, stateTypes>{
  constructor(props: propTypes) {
    super(props);
    this.state = {
      activeSlideIndex: 0,
    };
  }

  componentDidMount() {
    const { activeSlideIndex } = this.state;
    this.scheduleSlideTransition(activeSlideIndex);
  }

  componentWillUnmount() {
    this.unscheduleSlideTransition();
  }

  advanceSlide = () => {
    this.applySlideDelta(1);
  };

  applySlideDelta = (delta: number) => {
    const { activeSlideIndex } = this.state;

    const numSlides = slideLoiterTimes.length;
    let nextSlideIndex = (activeSlideIndex + delta) % numSlides;

    if (nextSlideIndex < 0) {
      nextSlideIndex += numSlides;
      nextSlideIndex = nextSlideIndex % numSlides;

      // Just in case.
      nextSlideIndex = Math.max(nextSlideIndex, 0);
    }

    this.scheduleSlideTransition(nextSlideIndex);
    this.setState({ activeSlideIndex: nextSlideIndex });
  };

  scheduleSlideTransition = (slideIndex: number) => {
    this.unscheduleSlideTransition();

    timeoutHandle = window.setTimeout(this.advanceSlide, slideLoiterTimes[slideIndex]);
  };

  unscheduleSlideTransition = () => {
    if (timeoutHandle) {
      window.clearTimeout(timeoutHandle);
      timeoutHandle = 0;
    }
  };

  render() {
    const { activeSlideIndex } = this.state;
    return (
      <SlidesContainer>
        <ArrowLeftIcon
          style={{ position: 'absolute', top: '50%', left: '20px', transform: 'translateY(-50%)', cursor: 'pointer' }}
          onClick={() => { this.applySlideDelta(-1); }}
        />
        <ArrowLeftIcon
          style={{ position: 'absolute', top: '50%', right: '20px', transform: 'translateY(-50%) rotate(180deg)', cursor: 'pointer' }}
          onClick={() => { this.applySlideDelta(1); }}
        />
        <SlideStats is-active={activeSlideIndex === 0}>
          <TitleProjectStats>See key project stats at a glance</TitleProjectStats>
          <PhoneImage alt="" src="images/phone-project-stats.png" />
        </SlideStats>
        <SlideReviews is-active={activeSlideIndex === 1}>
          <ReviewBox>
            <AndroidLogoIcon style={{ marginRight: '30px', verticalAlign: 'top' }} />
            <ReviewContainer>
              <ReviewName>Maxwell Curry</ReviewName>
              <div>
                <StarRow /><ReviewDateText>January 24, 2020</ReviewDateText>
                <ReviewDescription>
                  Liked it a lot better than Harvest, which I used for a while. Makes Harvest feel super clunky. This is really easy to use.
                </ReviewDescription>
              </div>
            </ReviewContainer>
          </ReviewBox>

          <ReviewBox add-top-space>
            <AndroidLogoIcon style={{ marginRight: '30px', verticalAlign: 'top' }} />
            <ReviewContainer>
              <ReviewName>Mike G</ReviewName>
              <div>
                <StarRow /><ReviewDateText>January 20, 2020</ReviewDateText>
                <ReviewDescription>
                  Super simple, modern way to track time/expenses/payments. Nice to use after trying Toggl.
                </ReviewDescription>
              </div>
            </ReviewContainer>
          </ReviewBox>

          <ReviewBox add-top-space>
            <AppleLogoIcon width={55} height={71} style={{ display: 'inline-block', marginRight: '30px', verticalAlign: 'top' }}/>
            <ReviewContainer>
              <ReviewName>Singsing250</ReviewName>
              <div>
                <StarRow /><ReviewDateText>January 20, 2020</ReviewDateText>
                <ReviewDescription>
                  Feels like a modern app after using Harvest. Really easy the way they set up tracking time and expenses on projects.
                </ReviewDescription>
              </div>
            </ReviewContainer>
          </ReviewBox>
        </SlideReviews>
        <SlideTrackTime is-active={activeSlideIndex === 2}>
          {/* Using a nbsp to try to occupy the same vertical space as the other two-line texts. */}
          <TitleTrackTimeTop>&nbsp;</TitleTrackTimeTop>
          <TitleTrackTime>Track time effortlessly</TitleTrackTime>
          <PhoneImage alt="" src="images/phone-timer-running.png" />
        </SlideTrackTime>
        <SlideProfitAndLoss is-active={activeSlideIndex === 3}>
          <TitleProfitLoss>See profit & loss, no math required</TitleProfitLoss>
          <PhoneImage alt="" src="images/phone-profit-and-loss-report.png" />
        </SlideProfitAndLoss>
      </SlidesContainer>
    );
  }
}

interface propTypes {
}

interface stateTypes {
  activeSlideIndex: number;
}

const TEXT_COLOR_WHITE = '#FAFAFA';

const SlidesContainer = styled.div({
  position: 'relative',
  width: '53%',
  height: '100%',
  userSelect: 'none',
  '.icon-arrow-left': {
    transition: 'opacity 0.2s',
    opacity: 0,
  },
  '&:hover': {
    '.icon-arrow-left': {
      opacity: 1,
    }
  },
  [media('small')]: {
    display: 'none',
  }
});

const Slide = styled.div((props) => ({
  display: props['is-active'] ? 'flex' : 'none',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  zIndex: props['is-active'] ? 1 : 0,
}));

const SlideStats = styled(Slide)({
  backgroundColor: COLORS.CYAN,
});

const SlideReviews = styled(Slide)({
  backgroundColor: COLORS.PURPLE,
  justifyContent: 'center',
});

const SlideTrackTime = styled(Slide)({
  backgroundColor: COLORS.BLUE,
});

const SlideProfitAndLoss = styled(Slide)({
  backgroundColor: COLORS.GRAY_LIGHT,
});

const TitleProjectStats = styled(Text)({
  color: TEXT_COLOR_WHITE,
  display: 'inline-block',
  width: '80%',
  // Use viewport width font size so the occupies two full lines any window size.
  fontSize: '4vw',
  fontWeight: 'bold',
  textAlign: 'center',
  marginTop: 40,
});

const StyledImage = styled.img({
  userSelect: 'none',
});

const PhoneImage = styled(StyledImage)({
  height: '70%',
  width: 'auto',
  marginTop: '15px',
});

const ReviewBox = styled.div((props) => ({
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  marginTop: props['add-top-space'] ? 30 : 0,
  paddingRight: '10px',
  paddingLeft: '10px',
}));

const StarRowContainer = styled.div({
  display: 'inline-block',
  verticalAlign: 'middle',
});

const ReviewContainer = styled.div({
  display: 'inline-block',
  width: '420px',
  maxWidth: 'calc(100% - 85px)',
  verticalAlign: 'top',
  marginTop: '20px',
});

const ReviewName = styled(Text)({
  fontSize: 18,
  color: TEXT_COLOR_WHITE,
});

const ReviewDateText = styled(Text)({
  marginLeft: '11px',
  fontSize: 11,
  color: TEXT_COLOR_WHITE,
  verticalAlign: 'middle',
});

const ReviewDescription = styled(Text)({
  display: 'block',
  fontSize: 18,
  color: TEXT_COLOR_WHITE,
  whiteSpace: 'normal'
});

const TitleTrackTime = styled(Text)({
  color: TEXT_COLOR_WHITE,
  display: 'inline-block',
  width: '80%',
  // Use viewport width font size so the occupies two full lines any window size.
  fontSize: '3.5vw',
  fontWeight: 'bold',
  textAlign: 'center',
});

const TitleTrackTimeTop = styled(TitleTrackTime)({
  marginTop: 40,
});

const TitleProfitLoss = styled(Text)({
  color: TEXT_COLOR_WHITE,
  display: 'inline-block',
  width: '70%',
  // Use viewport width font size so the occupies two full lines any window size.
  fontSize: '4vw',
  fontWeight: 'bold',
  textAlign: 'center',
  marginTop: 40,
});
